import { CountryCode } from "@silversea-ssc/dotcom-read-model-types/Common";
import { FareCode } from "@silversea-ssc/web-fares";
import FYCQueryBuilder, {
  FYCQuery
} from "components/FindYourCruise/FYCQueryBuilder";
import { isPresent } from "src/utils/checks";
import { splitCodesStringList, stringPresent } from "src/utils/strings";
import { match, P } from "ts-pattern";
import {
  Maybe,
  SanityCruiseData,
  SanityCruisesLaunch,
  SanityDestination,
  SanityExclusiveOfferPerPromo,
  SanityPortData,
  SanityPromoConfiguration,
  SanityShip,
  SanitySpecialVoyageData
} from "types/graphql-types";

type Args = {
  filterByCruiseType?: (string | undefined)[];
  filterByComboType?: (string | undefined)[];
  filterByFeatures?: (string | undefined)[];
  filterByCruise?: Maybe<
    | { data?: Maybe<Pick<SanityCruiseData, "cruiseCode">> }
    | { data?: Maybe<Pick<SanitySpecialVoyageData, "cruiseCode">> }
  >[];
  filterByCruiseCodes?: string;
  filterByDestination?: Maybe<Pick<SanityDestination, "destinationWebCode">>[];
  filterByOffer?: Maybe<
    Maybe<
      Pick<SanityExclusiveOfferPerPromo, "_id"> & {
        configuration?: Maybe<Pick<SanityPromoConfiguration, "algoliaCode">>;
      }
    >[]
  >;
  filterByShip?: Maybe<Maybe<Pick<SanityShip, "name">>[]>;
  filterByPorts?: Maybe<
    Maybe<{ data?: Maybe<Pick<SanityPortData, "code">> }>[]
  >;
  filterByCountries?: Maybe<
    Maybe<{
      ports?: Maybe<Maybe<{ data?: Maybe<Pick<SanityPortData, "code">> }>[]>;
    }>[]
  >;
  filterByCruiseGroups?: (string | undefined)[];
  filterByFareCodes?: Maybe<Maybe<string>[]>;
  filterByCruisesLaunch?: Maybe<Pick<SanityCruisesLaunch, "cruiseCodes">>;
  filterByLoadStress?: Maybe<Maybe<string>[]>;
  filterByDepartureYears?: Maybe<Maybe<string>[]>;
};

export const configToFYCQuery = (
  {
    filterByComboType,
    filterByCountries,
    filterByCruise,
    filterByCruiseCodes,
    filterByCruiseGroups,
    filterByCruiseType,
    filterByCruisesLaunch,
    filterByDestination,
    filterByFareCodes,
    filterByFeatures,
    filterByOffer,
    filterByPorts,
    filterByShip,
    filterByLoadStress,
    filterByDepartureYears
  }: Args,
  countryCode: CountryCode | undefined
): FYCQuery => {
  const fycQueryBuilder = new FYCQueryBuilder();
  const typedFilterByFareCodes = (filterByFareCodes?.filter(isPresent) ||
    []) as FareCode[];

  const allFiltersByCruiseCodes = [
    ...(filterByCruise || []).map((c) => c?.data?.cruiseCode),
    ...parseCruiseCodesToCruiseCodesArray(filterByCruiseCodes),
    ...parseCruiseCodesToCruiseCodesArray(filterByCruisesLaunch?.cruiseCodes)
  ].filter(stringPresent);

  if (allFiltersByCruiseCodes.length > 0) {
    fycQueryBuilder.filterByCruiseCodes(allFiltersByCruiseCodes);
  }

  if (filterByCruiseType?.length)
    fycQueryBuilder.filterByCruiseTypes(filterByCruiseType.filter(isPresent));

  if (filterByComboType?.length)
    fycQueryBuilder.filterByComboTypes(filterByComboType.filter(isPresent));

  if (filterByFeatures?.length)
    fycQueryBuilder.filterByFeatures(filterByFeatures.filter(isPresent));

  if (filterByCruiseGroups?.length)
    fycQueryBuilder.filterByCruiseGroups(
      filterByCruiseGroups.filter(isPresent)
    );

  if (typedFilterByFareCodes.length > 0)
    fycQueryBuilder.filterByFareCodes(typedFilterByFareCodes);

  if (filterByOffer?.length) {
    const promoCodes = filterByOffer
      .map((o) => o?.configuration?.algoliaCode)
      .filter(isPresent);
    if (promoCodes.length)
      fycQueryBuilder.filterByPromoCodes(promoCodes, countryCode);
  }

  if (filterByDestination?.length)
    fycQueryBuilder.filterByDestinationWebCodes(
      filterByDestination.map((d) => d?.destinationWebCode).filter(isPresent)
    );

  if (filterByShip?.length)
    fycQueryBuilder.filterByShipsName(
      filterByShip.map((s) => s?.name).filter(isPresent)
    );

  if (filterByPorts?.length) {
    const portCodes = filterByPorts.map((p) => p?.data?.code).filter(isPresent);
    fycQueryBuilder.filterByPorts(portCodes);
  }

  if (filterByCountries?.length) {
    const portCodes = filterByCountries
      .flatMap((country) => country?.ports?.map((p) => p?.data?.code))
      .filter(isPresent);
    fycQueryBuilder.filterByPorts(portCodes);
  }

  if (filterByLoadStress?.length) {
    fycQueryBuilder.filterByLoadStress(filterByLoadStress.filter(isPresent));
  }

  const uniqueFilterByDepartureYears = (filterByDepartureYears || []).filter(
    isPresent
  );
  if (uniqueFilterByDepartureYears.length) {
    fycQueryBuilder.filterByDepartureYears(uniqueFilterByDepartureYears);
  }
  return fycQueryBuilder.generate();
};

const parseCruiseCodesToCruiseCodesArray = (
  cruiseCodes: string | undefined
): string[] => {
  if (!cruiseCodes) return [];
  return splitCodesStringList(cruiseCodes);
};
